import { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon, Sparkles, Heart, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Programs', href: '/programs' },
  { name: 'Events', href: '/events' },
  { name: 'Kids Classes', href: '/education' },
  { name: 'Adult Classes', href: '/adult-education' },
  { name: 'Our Masjid', href: '/place-to-worship' },
  { name: 'Flyers', href: '/flyers' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Board', href: '/board' },
  { name: 'News', href: '/news' },
  { name: 'Contact', href: '/contact' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme, cursorEffect, toggleCursorEffect } = useTheme();
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <img
              src="/logo.png"
              alt="True North Muslim Community"
              className="h-16 w-16 rounded-xl object-contain bg-black p-1 ring-1 ring-secondary/40 group-hover:ring-secondary transition-all shadow-md"
            />
            <span className="hidden sm:block font-semibold text-foreground font-['Outfit'] group-hover:text-primary transition-colors">
              True North Muslim
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase()}`}
                className={`px-3 py-2 text-sm font-medium transition-colors animated-underline ${
                  location.pathname === item.href
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="flex items-center gap-2">
            {/* Cursor effect toggle - desktop only */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleCursorEffect}
              className="hidden md:flex"
              data-testid="cursor-toggle"
              aria-label={cursorEffect ? 'Disable cursor effect' : 'Enable cursor effect'}
            >
              <Sparkles className={`h-4 w-4 ${cursorEffect ? 'text-primary' : 'text-muted-foreground'}`} />
            </Button>

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 text-secondary" />
              ) : (
                <Moon className="h-4 w-4 text-primary" />
              )}
            </Button>

            {/* WhatsApp */}
            <a
              href="https://chat.whatsapp.com/BZpW0tw4MUuLAVuZmzBRDU?mode=gi_t"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex"
              data-testid="header-whatsapp"
            >
              <Button variant="ghost" size="icon" className="text-green-500 hover:text-green-400">
                <MessageCircle className="h-4 w-4" />
              </Button>
            </a>

            {/* Donate button */}
            <Link to="/donate" data-testid="header-donate-btn">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 glow-teal rounded-full px-6 hidden sm:flex items-center gap-2">
                <Heart className="h-4 w-4" />
                Donate
              </Button>
            </Link>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-72 bg-background border-l border-border z-50 lg:hidden shadow-2xl"
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between p-4 border-b border-border bg-card">
                <span className="font-semibold font-['Outfit'] text-foreground">Menu</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-80px)] bg-background">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                    className={`block px-4 py-3 rounded-xl transition-colors text-base font-medium ${
                      location.pathname === item.href
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'bg-card hover:bg-accent text-foreground hover:text-primary'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="pt-4 mt-4 border-t border-border space-y-3">
                  <Link to="/donate" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-donate-btn">
                    <Button className="w-full bg-primary text-primary-foreground rounded-full flex items-center justify-center gap-2 h-12">
                      <Heart className="h-4 w-4" />
                      Donate Now
                    </Button>
                  </Link>
                  <Link to="/volunteer" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-volunteer-btn">
                    <Button variant="outline" className="w-full rounded-full h-12">
                      Volunteer
                    </Button>
                  </Link>
                  <a
                    href="https://chat.whatsapp.com/BZpW0tw4MUuLAVuZmzBRDU?mode=gi_t"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    data-testid="mobile-whatsapp-btn"
                  >
                    <Button variant="outline" className="w-full rounded-full text-green-500 border-green-500/30 hover:bg-green-500/10 h-12">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Join WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
